import { useState, useCallback, useEffect } from 'react';
import { Sparkles, LayoutGrid, Table2, Settings, TrendingUp, MessageSquare, Handshake, Star, Loader2, RefreshCw, Zap } from 'lucide-react';
import { useRisingCreators } from '../hooks/useRisingCreators';
import type { RisingCreator, RisingCreatorStatus } from '../hooks/useRisingCreators';
import { supabase } from '../lib/supabase';
import CreatorCard from '../components/pepites/CreatorCard';
import CreatorDrawer from '../components/pepites/CreatorDrawer';
import PepitesFilters from '../components/pepites/PepitesFilters';
import CreatorsTable from '../components/pepites/CreatorsTable';
import { useNavigate } from 'react-router-dom';

type ViewMode = 'grid' | 'table';

function StatCard({ icon: Icon, label, value, color }: { icon: any; label: string; value: number | string; color: string }) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4 flex items-center gap-4">
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${color}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <div className="text-2xl font-bold text-gray-900">{value}</div>
        <div className="text-xs text-gray-500">{label}</div>
      </div>
    </div>
  );
}

export default function Pepites() {
  const navigate = useNavigate();
  const {
    creators,
    loading,
    error,
    filters,
    setFilters,
    page,
    setPage,
    reload,
    updateStatus,
    updateNotes,
    updateAssignedTo,
    bulkUpdateStatus,
    bulkAssign,
    loadSnapshots,
    convertToRecruitment,
    getStats,
    PAGE_SIZE,
  } = useRisingCreators();

  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [selectedCreator, setSelectedCreator] = useState<RisingCreator | null>(null);
  const [snapshots, setSnapshots] = useState<any[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [pendingCount, setPendingCount] = useState(0);
  const [enriching, setEnriching] = useState(false);
  const [enrichMessage, setEnrichMessage] = useState<string | null>(null);

  useEffect(() => {
    const fetchPendingCount = async () => {
      const { count } = await supabase
        .from('rising_creators')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'pending_enrichment');
      setPendingCount(count || 0);
    };
    fetchPendingCount();
  }, []);

  const handleEnrich = async () => {
    setEnriching(true);
    setEnrichMessage(null);
    try {
      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/enrich-rising-creators`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            'Content-Type': 'application/json',
          },
        }
      );
      const data = await res.json();
      if (data.success) {
        setEnrichMessage(`${data.enriched} créateur(s) enrichi(s), ${data.rejected} rejeté(s)`);
        setPendingCount(0);
        reload();
      } else {
        setEnrichMessage(data.message || data.error || 'Erreur');
      }
    } catch {
      setEnrichMessage('Erreur lors de l\'enrichissement');
    } finally {
      setEnriching(false);
    }
  };

  const stats = getStats();

  const handleDetails = useCallback(async (creator: RisingCreator) => {
    setSelectedCreator(creator);
    const snaps = await loadSnapshots(creator.id);
    setSnapshots(snaps);
  }, [loadSnapshots]);

  const handleContact = useCallback(async (creator: RisingCreator) => {
    await updateStatus(creator.id, 'contacted');
    setSelectedCreator(prev => prev?.id === creator.id ? { ...prev, status: 'contacted' } : prev);
  }, [updateStatus]);

  const handleReject = useCallback(async (creator: RisingCreator) => {
    await updateStatus(creator.id, 'rejected');
    setSelectedCreator(prev => prev?.id === creator.id ? { ...prev, status: 'rejected' } : prev);
  }, [updateStatus]);

  const handleStatusChange = useCallback(async (id: string, status: RisingCreatorStatus) => {
    await updateStatus(id, status);
    setSelectedCreator(prev => prev?.id === id ? { ...prev, status } : prev);
  }, [updateStatus]);

  const handleNotesChange = useCallback(async (id: string, notes: string) => {
    await updateNotes(id, notes);
    setSelectedCreator(prev => prev?.id === id ? { ...prev, notes } : prev);
  }, [updateNotes]);

  const handleAssignedToChange = useCallback(async (id: string, assignedTo: string) => {
    await updateAssignedTo(id, assignedTo);
    setSelectedCreator(prev => prev?.id === id ? { ...prev, assigned_to: assignedTo } : prev);
  }, [updateAssignedTo]);

  const handleConvertToRecruitment = useCallback(async (creator: RisingCreator) => {
    const ok = await convertToRecruitment(creator);
    if (ok) {
      setSelectedCreator(null);
      navigate('/app/recruitment');
    }
  }, [convertToRecruitment, navigate]);

  const handleSelectId = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSelectAll = () => {
    if (creators.every(c => selectedIds.has(c.id))) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(creators.map(c => c.id)));
    }
  };

  const handleBulkReject = async () => {
    await bulkUpdateStatus(Array.from(selectedIds), 'rejected');
    setSelectedIds(new Set());
  };

  const handleBulkAssign = async (member: string) => {
    await bulkAssign(Array.from(selectedIds), member);
    setSelectedIds(new Set());
  };

  return (
    <div className="flex flex-col h-full bg-gray-50 min-h-screen">
      <div className="bg-white border-b border-gray-200 px-6 py-5">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-amber-400 to-orange-500 rounded-xl flex items-center justify-center shadow-sm">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Pépites</h1>
              <p className="text-sm text-gray-500">Détection via followings de créateurs signés</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/app/pepites/config')}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 text-sm font-medium rounded-xl transition-colors"
            >
              <Settings className="w-4 h-4" />
              Pipeline de détection
            </button>
            {pendingCount > 0 && (
              <button
                onClick={handleEnrich}
                disabled={enriching}
                className="flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold rounded-xl transition-colors disabled:opacity-60"
              >
                {enriching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
                Enrichir ({pendingCount})
              </button>
            )}
            <button
              onClick={reload}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-xl transition-colors disabled:opacity-60"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Actualiser
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-6 space-y-5">
        {enrichMessage && (
          <div className="bg-amber-50 border border-amber-200 text-amber-800 text-sm px-4 py-3 rounded-xl">
            {enrichMessage}
          </div>
        )}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            icon={TrendingUp}
            label="Nouveaux cette semaine"
            value={stats.newThisWeek}
            color="bg-blue-100 text-blue-600"
          />
          <StatCard
            icon={MessageSquare}
            label="En cours de discussion"
            value={stats.inDiscussion}
            color="bg-orange-100 text-orange-600"
          />
          <StatCard
            icon={Handshake}
            label="Signés ce mois"
            value={stats.signedThisMonth}
            color="bg-green-100 text-green-600"
          />
          <StatCard
            icon={Star}
            label="Score moyen top 20"
            value={stats.avgScoreTop20}
            color="bg-amber-100 text-amber-600"
          />
        </div>

        <PepitesFilters filters={filters} onChange={setFilters} />

        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-500">
            {loading ? 'Chargement...' : `${creators.length} créateur(s) trouvé(s)`}
          </span>
          <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-xl p-1">
            <button
              onClick={() => setViewMode('grid')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                viewMode === 'grid' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <LayoutGrid className="w-3.5 h-3.5" /> Grille
            </button>
            <button
              onClick={() => setViewMode('table')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                viewMode === 'table' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <Table2 className="w-3.5 h-3.5" /> Tableau
            </button>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3">
            {error}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        ) : creators.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="w-16 h-16 bg-amber-100 rounded-2xl flex items-center justify-center mb-4">
              <Sparkles className="w-8 h-8 text-amber-500" />
            </div>
            <h3 className="text-lg font-semibold text-gray-700 mb-2">Aucune pépite pour le moment</h3>
            <p className="text-sm text-gray-500 max-w-sm">
              Les créateurs détectés via le pipeline followings apparaîtront ici. Ajoutez des comptes graines et lancez le pipeline depuis la configuration.
            </p>
            <button
              onClick={() => navigate('/app/pepites/config')}
              className="mt-6 flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-xl transition-colors"
            >
              <Settings className="w-4 h-4" />
              Configurer le pipeline
            </button>
          </div>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {creators.map(creator => (
              <CreatorCard
                key={creator.id}
                creator={creator}
                onDetails={handleDetails}
                onContact={handleContact}
                onReject={handleReject}
              />
            ))}
          </div>
        ) : (
          <CreatorsTable
            creators={creators}
            selectedIds={selectedIds}
            onSelectId={handleSelectId}
            onSelectAll={handleSelectAll}
            onDetails={handleDetails}
            onContact={handleContact}
            onReject={handleReject}
            onBulkReject={handleBulkReject}
            onBulkAssign={handleBulkAssign}
          />
        )}

        {creators.length === PAGE_SIZE && (
          <div className="flex justify-center">
            <button
              onClick={() => setPage(p => p + 1)}
              className="px-6 py-2.5 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 text-sm font-medium rounded-xl transition-colors"
            >
              Charger plus
            </button>
          </div>
        )}
      </div>

      <CreatorDrawer
        creator={selectedCreator}
        snapshots={snapshots}
        onClose={() => setSelectedCreator(null)}
        onStatusChange={handleStatusChange}
        onNotesChange={handleNotesChange}
        onAssignedToChange={handleAssignedToChange}
        onConvertToRecruitment={handleConvertToRecruitment}
      />
    </div>
  );
}
